import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
} from '@mui/material';
import { legalService, PLATFORM_SERVICE_CONTRACT_KEY, type LegalTemplate } from '../services/legalService';
import { LegalBlocks } from './legal/LegalBlocks';

interface TermsOfServiceProps {
  open: boolean;
  onClose: () => void;
  onAccept: () => void;
  parishName?: string;
  parishAddress?: string;
  parishCity?: string;
  parishState?: string;
  responsibleName?: string;
  responsibleEmail?: string;
  responsibleCPF?: string;
  parishCNPJ?: string;
  contratanteName?: string;
  contratanteAddress?: string;
  contratanteDocument?: string;
  representativeName?: string;
  representativeEmail?: string;
  representativeCpf?: string;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({
  open,
  onClose,
  onAccept,
  parishName,
  parishAddress,
  parishCity,
  parishState,
  responsibleName,
  responsibleEmail,
  responsibleCPF,
  parishCNPJ,
  contratanteName,
  contratanteAddress,
  contratanteDocument,
  representativeName,
  representativeEmail,
  representativeCpf,
}) => {
  const [template, setTemplate] = useState<LegalTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = contratanteName || parishName || '';
  const address =
    contratanteAddress ||
    [parishAddress, parishCity && parishState ? `${parishCity} - ${parishState}` : parishCity || parishState]
      .filter(Boolean)
      .join(', ');
  const document = contratanteDocument || parishCNPJ || '';
  const repName = representativeName || responsibleName || '';
  const repEmail = representativeEmail || responsibleEmail || '';
  const repCpf = representativeCpf || responsibleCPF || '';

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    legalService
      .getTemplate(PLATFORM_SERVICE_CONTRACT_KEY, {
        contratanteNome: name || '[Nome do organizador]',
        contratanteEndereco: address || '[Endereço]',
        contratanteDocumento: document || '[CNPJ]',
        representanteNome: repName || '[Responsável]',
        representanteEmail: repEmail || '[E-mail]',
        representanteCpf: repCpf || '[CPF]',
        signedAt: new Date().toLocaleDateString('pt-BR'),
      })
      .then((data) => {
        if (!cancelled) setTemplate(data);
      })
      .catch(() => {
        if (!cancelled) setError('Não foi possível carregar o contrato. Tente novamente.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, name, address, document, repName, repEmail, repCpf]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle>
        <Typography variant="h5" component="h2" align="center">
          {template?.title || 'Contrato de Prestação de Serviços'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: '60vh', overflow: 'auto', p: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : (
            <Paper elevation={0} sx={{ p: 2 }}>
              <LegalBlocks blocks={template?.blocks || []} />
            </Paper>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={onAccept} variant="contained" color="primary" disabled={loading || !!error}>
          Aceitar e continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsOfService;
